import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { useRedeliverWebhookMutation } from "@/pages/Private/redux/webhook/webhook.api";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { DeliveryDates } from "./DeliveryDates";
import { deliveryDateBound, redeliveryError } from "./deliveryPresentation";
import { useRedeliveryCounts } from "./useDeliveryData";

import type {
	RedeliveryCounts,
	RedeliveryRequest,
	WebhookDelivery,
} from "../schema/webhookDelivery";
import type { Webhook } from "../schema/webhook";

export function RedeliveryDialog({
	webhook,
	selection,
	dates,
	row,
	onClose,
	onResult,
	onError,
}: {
	webhook: Webhook;
	selection: RedeliveryRequest;
	dates: { from: string; to: string };
	row?: WebhookDelivery;
	onClose: () => void;
	onResult: (counts: RedeliveryCounts) => void;
	onError: (message: string) => void;
}) {
	const { t } = useTranslation("translation", { keyPrefix: "webhookHealth.deliveries" });
	const [range, setRange] = useState(dates);
	const [include, setInclude] = useState<"givenUp" | "all">("givenUp");
	const [times, setTimes] = useState({ from: "00:00", to: "23:59" });
	const from = deliveryDateBound(range.from, false, times.from);
	const to = deliveryDateBound(range.to, true, times.to);
	const isRange = selection.mode === "range";
	const validRange = !isRange || Boolean(from && to && times.from && times.to && from <= to);
	const query = useMemo(
		() => ({ webhookId: webhook.id, ...selection, ...(isRange ? { from, to, include } : {}) }),
		[webhook.id, selection, isRange, from, to, include]
	);
	const { data: counts, isFetching, isError, refetch } = useRedeliveryCounts(query, !validRange);
	const [redeliver] = useRedeliverWebhookMutation();
	const { run, isRunning } = useSingleFlight();
	const canSubmit =
		validRange && counts && !isFetching && !isError && !counts.overLimit && counts.resend > 0;
	const submit = () =>
		run(async () => {
			if (!canSubmit) {
				return;
			}

			try {
				onResult(await redeliver(query).unwrap());
			} catch (error) {
				onError(redeliveryError(error, t));
			}
		});

	return (
		<Modal
			cancelButtonText={t("cancel")}
			disableSave={isRunning}
			handleClose={isRunning ? undefined : onClose}
			handleSave={canSubmit ? submit : undefined}
			isLoading={isRunning}
			isOpened
			overflow
			size="md"
			submitButtonColor={ButtonColor.ACTION}
			submitButtonText={t("confirm", { count: counts?.resend ?? 0 })}
			takeFocus
			title={t(`${selection.mode}Title`)}
		>
			<div className="flex flex-col gap-4 text-sm text-text">
				<p className="text-text-meta">
					{webhook.name} ·{" "}
					{isRange
						? t("timezone", { zone: Intl.DateTimeFormat().resolvedOptions().timeZone })
						: selection.mode === "failed"
							? t("allDates")
							: webhook.url}
				</p>
				{isRange && (
					<>
						<DeliveryDates
							disabled={isRunning}
							from={range.from}
							to={range.to}
							onChange={setRange}
						/>
						<div className="flex flex-wrap gap-3">
							{(["from", "to"] as const).map((side) => (
								<label key={side} className="text-xs text-text-meta">
									{t(`${side}Time`)}
									<input
										className="input-styles block cursor-pointer rounded-[10px] px-3 py-2 text-sm text-text disabled:cursor-not-allowed"
										disabled={isRunning}
										type="time"
										value={times[side]}
										onChange={(event) => setTimes({ ...times, [side]: event.target.value })}
									/>
								</label>
							))}
						</div>
						<fieldset disabled={isRunning}>
							<legend className="mb-2">{t("which")}</legend>
							{(["givenUp", "all"] as const).map((value) => (
								<label key={value} className="mb-2 flex cursor-pointer items-center gap-2">
									<input
										checked={include === value}
										className="cursor-pointer"
										name="redelivery-include"
										type="radio"
										value={value}
										onChange={() => setInclude(value)}
									/>
									{t(value === "givenUp" ? "givenUpOnly" : "every")}
								</label>
							))}
						</fieldset>
					</>
				)}
				{!validRange ? (
					<p role="alert">{t("invalidRange")}</p>
				) : isError ? (
					<p role="alert">
						{t("previewFailed")}{" "}
						<button
							className="cursor-pointer text-brand-light"
							type="button"
							onClick={() => refetch()}
						>
							{t("retry")}
						</button>
					</p>
				) : isFetching || !counts ? (
					<p role="status">{t("loading")}</p>
				) : (
					<div className="rounded-lg border border-border-light bg-background-light p-4">
						<p className="font-semibold text-text">
							{t("counts", {
								resend: counts.resend,
								count: counts.resend,
								skipped: counts.skippedTotal,
							})}
						</p>
						<ul className="mt-3 space-y-1 text-text-meta">
							{(
								[
									["retrying", "retryingSkip"],
									["leadRemoved", "removedSkip"],
									["redeliveredAlready", "laterSkip"],
									["delivered", "deliveredSkip"],
								] as const
							).map(([key, label]) => (
								<li key={key}>
									<strong>{counts.skipped[key]}</strong> · {t(label)}
								</li>
							))}
						</ul>
						<p className="mt-3 text-xs text-text-meta">{t("limit", { max: counts.max })}</p>
						{counts.overLimit && (
							<p className="mt-3 font-semibold text-status-error" role="alert">
								{t("overLimit", { count: counts.resend, max: counts.max })}
							</p>
						)}
					</div>
				)}
				{row && (
					<p className="break-all text-text">
						{row.lead?.name} · {t("originalId", { id: row.deliveryId })}
					</p>
				)}
				<p className="text-text">{t("payload")}</p>
				<p className="text-text">{t("signing")}</p>
				<p className="rounded-lg border border-status-warning p-3 text-text">
					{t("duplicateWarning")}
				</p>
				<p className="text-xs text-text-meta">{t("retrySchedule")}</p>
			</div>
		</Modal>
	);
}
